import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { ContextMenuModule } from 'primeng/contextmenu';
import { Tooltip } from 'primeng/tooltip';
import { getFileIcon, getFileTypeColor, IFile } from '../../../../../core/interfaces/file.interface';

enum ViewMode {
    LIST,
    GRID
}

@Component({
    selector: 'app-file-list',
    standalone: true,
    templateUrl: './file-list.component.html',
    styleUrl: './file-list.component.scss',
    imports: [NgIf, NgForOf, NgClass, DatePipe, ContextMenuModule, Tooltip, FormsModule]
})
export class FileListComponent implements OnInit, OnChanges {
    constructor(private readonly cdr: ChangeDetectorRef) {}
    @Input() files: IFile[] = [];
    @Input() viewMode: ViewMode = ViewMode.GRID;
    @Output()
    renameFileConfirmed = new EventEmitter<{
        file: IFile;
        newName: string;
    }>();
    @Output() fileSelected = new EventEmitter<IFile>();
    @Output() moveFile = new EventEmitter<IFile>();
    @Output() downloadFile = new EventEmitter<IFile>();
    @Output() deleteFile = new EventEmitter<IFile>();
    @Output() fileProperties = new EventEmitter<IFile>();
    @ViewChildren('renameInput')
    renameInputs!: QueryList<ElementRef<HTMLInputElement>>;

    ViewMode = ViewMode;

    fileMenuItems: MenuItem[] = [];
    selectedFileForMenu: IFile | null = null;

    editingFileId: string | null = null;
    renameValue = '';

    @Input() selectedFiles: IFile[] = [];
    @Output() selectedFilesChange = new EventEmitter<IFile[]>();

    private selectedFileMap = new Map<string, IFile>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedFiles']) {
            this.initializeSelectionMap();
        }
    }

    private initializeSelectionMap(): void {
        this.selectedFileMap.clear();
        this.selectedFiles?.forEach((file) => this.selectedFileMap.set(file.id, file));
    }

    isSelected(file: IFile): boolean {
        return this.selectedFileMap.has(file.id);
    }

    toggleFileSelection(file: IFile, checked: boolean): void {
        if (checked) {
            this.selectedFileMap.set(file.id, file);
        } else {
            this.selectedFileMap.delete(file.id);
        }
        this.selectedFilesChange.emit(Array.from(this.selectedFileMap.values()));
    }

    ngOnInit(): void {
        this.console.log('Inicializando FileListComponent com arquivos:', this.files);
        this.fileMenuItems = [
            { label: 'Abrir', icon: 'pi pi-folder-open', command: () => this.selectedFileForMenu && this.fileSelected.emit(this.selectedFileForMenu) },
            {
                label: 'Renomear',
                icon: 'pi pi-pencil',
                command: () => {
                    setTimeout(() => this.startRename(this.selectedFileForMenu));
                }
            },
            { label: 'Mover para outro diretório', icon: 'pi pi-arrow-right-arrow-left', command: () => this.selectedFileForMenu && this.moveFile.emit(this.selectedFileForMenu) },
            { label: 'Baixar', icon: 'pi pi-download', command: () => this.selectedFileForMenu && this.downloadFile.emit(this.selectedFileForMenu) },
            { label: 'Mover para lixeira', icon: 'pi pi-trash', command: () => this.selectedFileForMenu && this.deleteFile.emit(this.selectedFileForMenu) },
            { label: 'Propriedades', icon: 'pi pi-info-circle', command: () => this.selectedFileForMenu && this.fileProperties.emit(this.selectedFileForMenu) }
        ];
    }

    selectFile(file: IFile): void {
        this.fileSelected.emit(file);
    }

    startRename(file: IFile | null): void {
        if (!file) {
            return;
        }

        this.editingFileId = file.id;
        this.renameValue = this.getNormalizedFileName(file.name);

        this.cdr.detectChanges();

        setTimeout(() => {
            const target = this.renameInputs.find((i) => i.nativeElement.dataset['fileId'] === file.id);

            target?.nativeElement.focus();
            target?.nativeElement.select();
        });
    }

    cancelRename(): void {
        this.editingFileId = null;
        this.renameValue = '';
    }

    confirmRename(file: IFile): void {
        const newName = this.renameValue?.trim();

        if (!newName) {
            this.cancelRename();
            return;
        }

        const extension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
        const newFileName = `${newName}${extension}`;

        if (newFileName === file.name) {
            this.cancelRename();
            return;
        }

        this.renameFileConfirmed.emit({
            file,
            newName: newFileName
        });

        this.editingFileId = null;
    }

    onFileContextMenu(event: MouseEvent, file: IFile, menu: any): void {
        event.preventDefault();

        this.selectedFileForMenu = file;

        menu.show(event);
    }

    getNormalizedFileName(fileName: string): string {
        return fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;
    }

    getTooltipText(file: any): string {
        if (file.status === 'PENDING_UPLOAD' && file.retryCount < 3) {
            return 'Sincronizando...';
        }
        if (file.status === 'PENDING_UPLOAD' && file.retryCount >= 3) {
            return 'Upload falhou permanentemente. Clique com botão direito para deletar.';
        }
        if (file.status === 'ERROR' && file.retryCount >= 3) {
            return 'Erro permanente. Clique com botão direito para deletar.';
        }
        if (file.status === 'ERROR') {
            return `Erro (${file.retryCount}/3). Tentando novamente...`;
        }
        return file.name;
    }

    delete(file: any, event: Event): void {
        event.stopPropagation();
        this.deleteFile.emit(file);
    }

    formatFileSize(size: number): string {
        const units = ['Bytes', 'KB', 'MB', 'GB'];
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }
        return `${size.toFixed(0)} ${units[unitIndex]}`;
    }

    getUpdateOrCreationBy(file: IFile): string {
        if (file.updatedBy) {
            return file.updatedBy.name;
        }
        if (file.createdBy) {
            return file.createdBy.name;
        }
        return 'Sistema';
    }

    onCardClick(file: IFile): void {
        if (this.editingFileId === file.id) {
            return;
        }

        if (file.status === 'PENDING_UPLOAD') {
            return;
        }

        if (file.retryCount >= 3) {
            return;
        }

        this.selectedFileForMenu = file;
        this.selectFile(file);
    }

    @HostListener('window:keydown.f2', ['$event'])
    onF2Shortcut(event: Event): void {
        if (this.editingFileId || !this.selectedFileForMenu) {
            return;
        }

        const keyboardEvent = event as KeyboardEvent;
        keyboardEvent.preventDefault();
        this.startRename(this.selectedFileForMenu);
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        if (this.editingFileId) {
            this.cancelRename();
        }
    }

    getFileSize(file: IFile): string {
        return this.formatFileSize(file.size);
    }

    protected readonly getFileTypeColor = getFileTypeColor;
    protected readonly getFileIcon = getFileIcon;
    protected readonly console = console;
    protected readonly Math = Math;
}
